<script setup lang="ts">
import Calendar from "~/components/icons/Calendar.vue";
import Documents from "~/components/icons/Documents.vue";
import Memoweb from "~/components/icons/Memoweb.vue";
import { AREAS_MAP } from "~/lib/const";
import { link } from "~/lib/utils/contents";
import { processError } from "~/lib/utils/errors";
import { defineSeo } from "~/lib/utils/seo";

const omniaSdk = useOmniaSdk();
const route = useRoute();
const areaSlug = route.params.slug as string;
const refMenus = useRefMenus();

const {
  data: areaData,
  status: areaStatus,
  error,
} = await useAsyncData(`area-${areaSlug}`, async () => {
  try {
    return await omniaSdk.v1.areas.get(areaSlug);
  } catch (error) {
    processError(error);
  }
});

if (error.value) processError(error.value);

const { data, status } = useAsyncData(`area-${areaSlug}-full`, async () => {
  try {
    return await omniaSdk.v1.areas.get(areaSlug, { full: true });
  } catch (error) {
    processError(error);
  }
});

const { data: areaContents, status: statusContents } = useAsyncData(`area-${areaSlug}-contents`, async () => {
  try {
    return await omniaSdk.v1.contents.all({ area: areaSlug }, { pagination: "cursor" }, { types: ["notizie", "scadenzario", "modelli"] });
  } catch (error) {
    processError(error);
  }
});

defineSeo(areaData.value);
</script>

<template>
  <FrontBreadcrumHero :breadcrumbs="[{ title: areaData?.title }]" :title="areaData?.title" :search="true" :skeleton="!areaData && areaStatus === 'pending'" :search-filters="{ area: [areaSlug] }">
    <template #icon>
      <component v-if="areaData" :is="AREAS_MAP[areaData.id]?.icon" :class="['w-12 h-12', 'text-' + AREAS_MAP[areaData.id].color]" :active="true"></component>
    </template>
  </FrontBreadcrumHero>

  <div class="main-content mb-10">
    <h2 class="flex items-center mb-8">
      <Icon name="fluent:text-bullet-list-ltr-16-regular" class="h-8 w-8 text-themeGray me-4" /><span class="text-3xl font-semibold">Indice ragionato dell'ufficio {{ data?.title }}</span>
    </h2>

    <div
      class="mb-8 print:mb-6 prose prose-a:text-primary max-w-full font-semibold text-lg print:text-base print:italic print:font-normal"
      v-if="data?.abstract?.length"
      v-html="data.abstract.replace(/\n/g, '<br>')"
    ></div>

    <div class="lg:grid grid-cols-4 gap-10 xl:gap-20">
      <div class="col-span-3 mb-16 lg:mb-0">
        <div v-if="status === 'pending'">
          <Skeleton v-for="i in Array(20).keys()" :key="i" class="mb-2 h-16" />
        </div>
        <FrontMenuTree v-else-if="data?.menu" :menu="data?.menu" :area="data" :opened="refMenus" />
      </div>

      <div class="col-span-1">
        <div class="grid gap-10">
          <div v-if="statusContents === 'pending'">
            <Skeleton v-for="i in Array(3).keys()" :key="i" class="h-96 mb-10" />
          </div>
          <FrontSideCard title="Informazioni" subtitle="dal Memoweb" :icon="Memoweb" v-if="areaContents?.notizie?.length" :cta-link="link({ area: data }) + '/notizie'">
            <FrontContentItem
              :link-config="{ area: data }"
              :compact="true"
              :show-children="false"
              :content="content"
              :show-areas="false"
              v-for="content in areaContents?.notizie"
              :key="`notizie-${content.id}`"
            />
          </FrontSideCard>

          <FrontSideCard title="Scadenze" :icon="Calendar" theme="secondary" v-if="areaContents?.scadenzario?.length" :cta-link="link({ area: data }) + '/scadenzario'">
            <FrontContentItem
              :link-config="{ area: data }"
              :compact="true"
              :show-children="false"
              :content="content"
              :show-areas="false"
              :calendar="true"
              v-for="content in areaContents?.scadenzario"
              :key="`scadenzario-${content.id}`"
            />
          </FrontSideCard>

          <FrontSideCard title="Ultimi modelli aggiornati" :icon="Documents" v-if="areaContents?.modelli?.length">
            <FrontContentItem
              :link-config="{ area: data }"
              :compact="true"
              :show-children="false"
              :content="content"
              :show-areas="false"
              v-for="content in areaContents?.modelli"
              :key="`modelli-${content.id}`"
            />
          </FrontSideCard>
        </div>
      </div>
    </div>
  </div>

  <AdminLinkToAdmin :config="{ area: data }" />
</template>
